// ===========================================
// Brand Colors - Central Constants
// ===========================================
// Consolidated brand color definitions for consistent use across components

// Primary brand colors from theme
export const brandColors = {
  // Primary
  primary: '#4580E4',
  primaryLight: '#a3d9f6',
  primaryDark: '#3899ec',

  // Secondary
  secondary: '#FFCE00',
  secondaryLight: '#FFE066',
  secondaryDark: '#E6B800',

  // Accent colors
  mint: '#96DAC9',
  coral: '#FFAE9E',
  cream: '#FCF6E6',

  // Character colors
  alice: '#FFB6C1', // Preschool
  steve: '#FFCE00', // Kids
  liam: '#4580E4', // Teens
  floyd: '#96DAC9', // Adult

  // Semantic
  error: '#ff4040',
  success: '#96DAC9',
  warning: '#FFAE9E',
  info: '#4580E4',

  // Text
  textPrimary: '#080808',
  textSecondary: '#68707B', // WCAG AA on white and cream (was #9DA5AF)

  // Backgrounds
  bgDefault: '#ffffff',
  bgPaper: '#FCF6E6',
  bgMuted: '#e0e0e0',
};

// Status color variants (for chips, badges, etc.)
export const statusColors = {
  // Success variants (green-mint tones)
  successBg: '#c5ebe2',
  successText: '#5cb399',

  // Warning variants (coral-red tones)
  warningBg: '#ffd4cc',
  warningText: '#e67761',

  // Info variants (blue tones)
  infoBg: '#a3d9f6',
  infoText: '#4580E4',

  // Pending variants (yellow tones)
  pendingBg: '#FFE066',
  pendingText: '#E6B800',

  // Muted/disabled
  mutedBg: '#e0e0e0',
  mutedText: '#68707B',
};

// Invoice status color mapping
export const invoiceStatusColors = {
  DRAFT: { bg: brandColors.cream, text: brandColors.textSecondary },
  SENT: { bg: statusColors.infoBg, text: statusColors.infoText },
  PAID: { bg: brandColors.mint, text: brandColors.textPrimary },
  PARTIALLY_PAID: { bg: brandColors.secondary, text: brandColors.textPrimary },
  OVERDUE: { bg: brandColors.coral, text: brandColors.error },
  CANCELLED: { bg: statusColors.mutedBg, text: statusColors.mutedText },
  REFUNDED: { bg: statusColors.mutedBg, text: statusColors.mutedText },
} as const;

// Meet & Greet status colors
export const meetAndGreetStatusColors = {
  PENDING_APPROVAL: { bg: statusColors.warningBg, text: statusColors.warningText },
  APPROVED: { bg: statusColors.successBg, text: statusColors.successText },
  SCHEDULED: { bg: statusColors.successBg, text: statusColors.successText },
  COMPLETED: { bg: brandColors.mint, text: brandColors.textPrimary },
  CANCELLED: { bg: statusColors.mutedBg, text: statusColors.mutedText },
} as const;

// Lesson type colors
export const lessonTypeColors = {
  INDIVIDUAL: brandColors.primary,
  GROUP: brandColors.mint,
  BAND: brandColors.coral,
  HYBRID: brandColors.secondary,
} as const;

// File type colors for Drive/Resources
export const fileTypeColors = {
  image: { bg: '#fce4ec', text: '#c2185b' },
  audio: { bg: '#e3f2fd', text: '#1565c0' },
  video: { bg: '#fff3e0', text: '#e65100' },
  pdf: { bg: '#ffebee', text: '#c62828' },
  spreadsheet: { bg: '#e8f5e9', text: '#2e7d32' },
  document: { bg: statusColors.infoBg, text: statusColors.infoText },
  default: { bg: statusColors.mutedBg, text: statusColors.mutedText },
} as const;

// ===========================================
// COLOR PICKER UTILITIES
// ===========================================

// Color picker preset options for admin forms (brand colors)
export const colorPickerPresets = [
  { value: '#4580E4', label: 'Blue', textColor: '#ffffff' },
  { value: '#FFCE00', label: 'Yellow', textColor: '#080808' },
  { value: '#96DAC9', label: 'Mint', textColor: '#080808' },
  { value: '#FFAE9E', label: 'Coral', textColor: '#080808' },
  { value: '#FCF6E6', label: 'Cream', textColor: '#080808' },
] as const;

/**
 * Calculate appropriate text color (black or white) for a given background color
 * Uses relative luminance formula for WCAG contrast
 */
export function getContrastTextColor(hexColor: string): string {
  // Remove # if present
  const hex = hexColor.replace('#', '');

  // Convert to RGB
  const r = parseInt(hex.substring(0, 2), 16);
  const g = parseInt(hex.substring(2, 4), 16);
  const b = parseInt(hex.substring(4, 6), 16);

  // Calculate relative luminance (WCAG formula)
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;

  // Return black for light backgrounds, white for dark backgrounds
  return luminance > 0.5 ? '#080808' : '#ffffff';
}

// Default fallback colors for lesson types when no custom color is set
export const defaultLessonTypeColors: Record<string, string> = {
  INDIVIDUAL: '#4580E4',
  GROUP: '#96DAC9',
  BAND: '#FFAE9E',
  HYBRID: '#FFCE00',
};
